import { Plus } from "lucide-react";
import React, { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { generateId } from "@repo/core";
import { AppCard, KpiCard, KpiIcons, Money, SectionTitle } from "../components/ui/AppCard";
import { ChartExportFrame } from "../components/ChartExportFrame";
import { useCurrency } from "../hooks/useCurrency";
import { useAccounts, useNetWorthLogs } from "../hooks/useBudgetData";
import { useT } from "../i18n/useT";
import { db } from "../lib/db";
import { handleFormSubmit, onEnterToSubmitKeyDown } from "../lib/formKeyboard";
import {
  cropToPopulatedRange,
  exportCanvasChartPng,
  CHART_ASSETS_GREEN,
  CHART_DEBTS_RED,
  CHART_NET_WORTH,
  resolveThemeChartColors,
} from "../lib/exportUtils";
import { SEMANTIC } from "../lib/semanticColors";
import { motion } from "framer-motion";
import { cn } from "@repo/core";

// ── Custom rich tooltip ────────────────────────────────────
function NetWorthTooltip({ active, payload, label }: TooltipProps<number, string>) {
  const { format } = useCurrency();
  const { t } = useT();
  if (!active || !payload?.length) return null;
  const nw     = Number(payload.find((p) => p.dataKey === "NetWorth")?.value ?? 0);
  const assets = Number(payload.find((p) => p.dataKey === "Assets")?.value ?? 0);
  const debts  = Number(payload.find((p) => p.dataKey === "Debts")?.value ?? 0);

  return (
    <div
      className="rounded-xl border border-border bg-card shadow-lg px-4 py-3 text-xs min-w-[150px]"
      style={{ boxShadow: "var(--card-shadow-hover)" }}
    >
      <p className="font-semibold text-foreground mb-2">{label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: CHART_NET_WORTH }} />
            {t("nw.netWorth")}
          </span>
          <span className="font-bold text-foreground">{format(nw)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: CHART_ASSETS_GREEN }} />
            {t("nw.assets")}
          </span>
          <span className={cn("font-medium", SEMANTIC.positive.text)}>{format(assets)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: CHART_DEBTS_RED }} />
            {t("nw.debts")}
          </span>
          <span className={cn("font-medium", SEMANTIC.negative.text)}>{format(debts)}</span>
        </div>
      </div>
    </div>
  );
}

export function NetWorthPage() {
  const { format, symbol } = useCurrency();
  const { t } = useT();
  const logs     = useNetWorthLogs();
  const accounts = useAccounts();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    monthYear:   new Date().toISOString().slice(0, 7),
    assetsTotal: "",
    debtsTotal:  "",
  });

  const latest  = logs[logs.length - 1];
  const prev    = logs[logs.length - 2];
  const netWorth = latest ? latest.assetsTotal - latest.debtsTotal : 0;
  const prevNW   = prev   ? prev.assetsTotal   - prev.debtsTotal   : null;
  const nwChange = prevNW !== null ? netWorth - prevNW : null;

  const chartData = logs.map((l) => ({
    month:    l.monthYear.slice(5) + "/" + l.monthYear.slice(2, 4),
    Assets:   l.assetsTotal,
    Debts:    l.debtsTotal,
    NetWorth: l.assetsTotal - l.debtsTotal,
  }));

  const exportNetWorthPng = () => {
    const cropped = cropToPopulatedRange(
      chartData,
      (r) => r.Assets > 0 || r.Debts > 0 || Math.abs(r.NetWorth) > 0
    );
    const colors = resolveThemeChartColors();
    const labels = cropped.map((d) => d.month);
    const first = logs[0]?.monthYear ?? "";
    const last = logs[logs.length - 1]?.monthYear ?? "";
    exportCanvasChartPng({
      filename: "net-worth-trend",
      title: t("nw.trend"),
      labels,
      series: [
        { label: t("nw.netWorth"), color: colors.netWorth, values: cropped.map((d) => d.NetWorth), dashed: true },
        { label: t("nw.assets"), color: colors.assets, values: cropped.map((d) => d.Assets) },
        { label: t("nw.debts"), color: colors.destructive, values: cropped.map((d) => d.Debts) },
      ],
      watermark: `BudgetOS · ${first || "—"}–${last || "—"}`,
      kind: "lines",
      formatTick: (n) => format(n, true),
    });
  };

  const totalAssets = accounts
    .filter((a) => ["asset","savings","checking"].includes(a.type))
    .reduce((s, a) => s + a.initialBalance, 0);
  const totalDebts = accounts
    .filter((a) => a.type === "debt")
    .reduce((s, a) => s + Math.abs(a.initialBalance), 0);

  const handleAddLog = async () => {
    if (!form.monthYear || !form.assetsTotal) return;
    await db.netWorthLogs.add({
      id:          generateId(),
      monthYear:   form.monthYear,
      assetsTotal: parseFloat(form.assetsTotal),
      debtsTotal:  parseFloat(form.debtsTotal) || 0,
    });
    setShowForm(false);
    setForm({ monthYear: new Date().toISOString().slice(0, 7), assetsTotal: "", debtsTotal: "" });
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 w-full max-w-full overflow-x-hidden">
      <SectionTitle
        action={
          <button onClick={() => setShowForm((v) => !v)} className="app-btn min-h-[44px] sm:min-h-0">
            <Plus size={13} /> {t("nw.logMonth")}
          </button>
        }
      >
        {t("nw.title")}
      </SectionTitle>

      {/* Hero KPI */}
      <KpiCard
        label={t("nw.current")}
        value={format(netWorth)}
        color="mint"
        icon={<KpiIcons.assets size={18} />}
        size="lg"
        index={0}
        sub={
          nwChange !== null
            ? `${nwChange >= 0 ? "▲" : "▼"} ${format(Math.abs(nwChange))} ${t("nw.vsLastMonth")}`
            : undefined
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label={t("nw.assets")}
          value={format(latest?.assetsTotal ?? totalAssets, true)}
          color={SEMANTIC.positive.kpi}
          icon={<KpiIcons.assets size={16} />}
          index={1}
        />
        <KpiCard
          label={t("nw.debts")}
          value={format(latest?.debtsTotal ?? totalDebts, true)}
          color={SEMANTIC.negative.kpi}
          icon={<KpiIcons.debts size={16} />}
          index={2}
        />
        <KpiCard
          label={t("nw.debtRatio")}
          value={latest ? `${((latest.debtsTotal / Math.max(latest.assetsTotal, 1)) * 100).toFixed(1)}%` : "—"}
          color={SEMANTIC.caution.kpi}
          icon={<KpiIcons.rate size={16} />}
          index={3}
        />
        <KpiCard
          label={t("nw.monthsTracked")}
          value={logs.length}
          color={SEMANTIC.neutral.kpi}
          icon={<KpiIcons.calendar size={16} />}
          index={4}
        />
      </div>

      {/* Log form */}
      {showForm && (
        <AppCard color="lemon" padding="md">
          <form
            onKeyDown={onEnterToSubmitKeyDown}
            onSubmit={(e) => handleFormSubmit(e, () => handleAddLog())}
          >
          <p className="font-semibold text-sm mb-3">{t("nw.logForm")}</p>
          <div className="grid grid-cols-1 min-[390px]:grid-cols-3 gap-3">
            {[
              { label: t("nw.month"), field: "monthYear", type: "month" },
              { label: t("nw.totalAssets", { symbol }), field: "assetsTotal", type: "number" },
              { label: t("nw.totalDebts", { symbol }), field: "debtsTotal", type: "number" },
            ].map(({ label, field, type }) => (
              <div key={field} className="min-w-0">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">{label}</label>
                <input
                  type={type}
                  value={(form as Record<string, string>)[field]}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  className="app-input w-full min-h-[44px] sm:min-h-0"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button type="submit" className="app-btn">{t("nw.saveLog")}</button>
            <button type="button" onClick={() => setShowForm(false)} className="app-btn-outline">{t("common.cancel")}</button>
          </div>
          </form>
        </AppCard>
      )}

      {/* Smooth Area Trend chart with pastel gradients */}
      <AppCard padding="md">
        <ChartExportFrame title={t("nw.trend")} onExport={exportNetWorthPng}>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
            <defs>
              <linearGradient id="nwGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={CHART_NET_WORTH} stopOpacity={0.35} />
                <stop offset="95%" stopColor={CHART_NET_WORTH} stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="assetsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={CHART_ASSETS_GREEN} stopOpacity={0.2} />
                <stop offset="95%" stopColor={CHART_ASSETS_GREEN} stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="debtsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={CHART_DEBTS_RED} stopOpacity={0.2} />
                <stop offset="95%" stopColor={CHART_DEBTS_RED} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            {/* width >= ~64: compact ES labels like "80 mil US$" must not clip (left:-10 made "20 mil" read as "0 mil") */}
            <YAxis
              width={68}
              tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => format(v, true)}
            />
            <Tooltip content={<NetWorthTooltip />} />
            <Area
              type="monotone"
              dataKey="Assets"
              stroke={CHART_ASSETS_GREEN}
              strokeWidth={1.5}
              fillOpacity={1}
              fill="url(#assetsGradient)"
              strokeDasharray="4 2"
              name={t("nw.assets")}
            />
            <Area
              type="monotone"
              dataKey="Debts"
              stroke={CHART_DEBTS_RED}
              strokeWidth={1.5}
              fillOpacity={1}
              fill="url(#debtsGradient)"
              strokeDasharray="4 2"
              name={t("nw.debts")}
            />
            <Area
              type="monotone"
              dataKey="NetWorth"
              stroke={CHART_NET_WORTH}
              strokeWidth={2.5}
              strokeDasharray="7 4"
              fillOpacity={1}
              fill="url(#nwGradient)"
              name={t("nw.netWorth")}
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex gap-5 mt-3 justify-center">
          {[
            { label: t("nw.netWorth"), color: CHART_NET_WORTH, solid: false },
            { label: t("nw.assets"),   color: CHART_ASSETS_GREEN, solid: false },
            { label: t("nw.debts"),    color: CHART_DEBTS_RED, solid: false },
          ].map((l) => (
            <div key={l.label} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <div
                className="w-4 h-0 border-t-2 border-dashed"
                style={{ borderColor: l.color }}
              />
              {l.label}
            </div>
          ))}
        </div>
        </ChartExportFrame>
      </AppCard>

      {/* Accounts breakdown */}
      <AppCard padding="md">
        <SectionTitle>{t("nw.accounts")}</SectionTitle>
        {accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t("empty.accounts.networth")}
          </p>
        ) : (
        <div className="space-y-0 divide-y divide-border">
          {accounts.map((acc) => {
            const isDebt = acc.type === "debt";
            return (
              <div key={acc.id} className="flex items-center justify-between gap-3 py-2.5 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{acc.name}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{acc.type}</p>
                </div>
                <Money
                  amount={acc.initialBalance}
                  className={cn(
                    "shrink-0 tabular-nums",
                    isDebt ? SEMANTIC.negative.text : SEMANTIC.positive.text
                  )}
                  colored
                />
              </div>
            );
          })}
        </div>
        )}
      </AppCard>
    </div>
  );
}
